import { useMemo, useState } from 'react'
import { DepositeTypesResponse } from '@http/api-models/DepositeTypesResponse'

type Program = 'Первое сбережение' | 'Премиум сбережения' | 'Базовые сбережения'
type PaymentType = 'MONTHLY'
type Duration = '6' | '12' | '24'
interface ProgramDetails {
	firstDeposite?: boolean
	durations: Array<Duration>
	types: Array<PaymentType>
}

export const programOptions: Array<Program> = [
	'Первое сбережение',
	'Премиум сбережения',
	'Базовые сбережения',
]

const MIN_STEP = 1 // начальное значение range-input
const MILLION_1 = 1000000
const MAX_STEP = 540 // количество всех чисел (1 тыс., 5 тыс., 10 тыс., 100тыс и т.д.)
const MIN_AMOUNT = 500000
const THOUSANDS_100 = 100000

export const getProgramDetails = (amount?: number): ProgramDetails => {
	if (amount && amount >= THOUSANDS_100 * 5 && amount < MILLION_1 * 20 + 1) {
		return {
			durations: ['6', '12'],
			types: ['MONTHLY'],
		}
	} else {
		return {
			durations: ['6'],
			types: ['MONTHLY'],
		}
	}
}

const getInterestRateValue = (amount?: number) => {
	if (amount == null) return [0, 0]
	const interestValue = amount > MILLION_1 * 20 ? 26 : 24

	return [interestValue, (amount * (interestValue / 100)) / 12]
}

export const durationsOptions: Array<Duration> = ['6', '12', '24']
const durationLabels = { '6': '6 месяцев', '12': '12 месяцев', '24': '24 месяцев' }
export const getDurationLabel = (o: { value: string }): string => durationLabels[o.value] || ''

export const paymentsOptions: Array<PaymentType> = ['MONTHLY']
const paymentsLabels = { MONTHLY: 'Ежемесячно' }
export const getPaymentsLabel = (o: { value: string }): string => paymentsLabels[o.value] || ''

// получение значения range-input из суммы
export const getStepsFromAmount = (amount: number) => {
	let tempAmount = amount
	let currentStep = 0

	while (tempAmount > 0) {
		if (tempAmount >= MILLION_1 + THOUSANDS_100) {
			tempAmount -= THOUSANDS_100
			currentStep++
			continue
		}
		if (tempAmount > 1000000 && tempAmount < 1100000) {
			tempAmount = 1000000
		}
	}

	return currentStep
}

export const getAmountFromSteps = (steps: number) => {
	if (steps <= 1) return THOUSANDS_100 * 5

	let amount = THOUSANDS_100 * 5

	for (let i = 0; i < steps; i++) {
		if (i < 50) {
			amount += THOUSANDS_100 / 10
			continue
		}
		amount += THOUSANDS_100 // до 5000000
	}

	return amount
}

interface UseCalcValue {
	programDetails: ProgramDetails
	rangeValue: number
	min: number
	max: number
	handleChange: (value: number) => void
	duration: Duration
	setDuration: (value: Duration) => void
	paymentType: PaymentType
	setPaymentType: (value: PaymentType) => void
	interestRate: number
	income: number
}

export function useCalc(): UseCalcValue {
	const [amount, setAmount] = useState<number>(MIN_AMOUNT)
	const [rangeValue, setRangeValue] = useState<number>(1)
	const programDetails = getProgramDetails(amount)
	const [duration, setDuration] = useState<Duration>(programDetails.durations[0])
	const [initialInterestRate, initialIncome] = getInterestRateValue(amount)
	const [paymentType, setPaymentType] = useState<PaymentType>(programDetails.types[0])
	const [interestRate, setInterestRate] = useState<number>(initialInterestRate)
	const [income, setIncome] = useState<number>(initialIncome)

	const handleChange = (steps: number) => {
		setRangeValue(steps)
		const amount = getAmountFromSteps(steps)
		const [interestRate, income] = getInterestRateValue(amount)
		setInterestRate(interestRate)
		setIncome(income)
		setAmount(amount)
	}

	const result = useMemo<UseCalcValue>(
		() => ({
			min: MIN_STEP,
			max: MAX_STEP,
			programDetails,
			rangeValue,
			handleChange,
			duration,
			setDuration,
			paymentType,
			setPaymentType,
			interestRate,
			income,
		}),
		[programDetails, amount, duration, paymentType, interestRate]
	)
	return result
}
